import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { IFormCondition, IFormConditionAction, IFormQuestion } from '@fi-shared/fi-form-shared/fi-form-shared';
import { isDefined } from '@fi-shared/utils';
import { FormQuestionAction } from '@fi-shared/fi-form-shared/fi-form-enums';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiTooltipButtonIconColors } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';

@Component({
    selector: 'fi-form-logic-condition-actions',
    templateUrl: './fi-form-logic-condition-actions.component.html',
    styleUrls: ['./fi-form-logic-condition-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-form-logic-condition-actions' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiFormLogicConditionActionsComponent }],
    standalone: false,
})
export class FiFormLogicConditionActionsComponent extends FiComponentsContainer {
    @Input() formQuestions: IFormQuestion[] = [];
    @Input() condition: IFormCondition;
    @Input() selectedLanguageId: number;
    readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    readonly FormQuestionAction = FormQuestionAction;

    get selectedSourceQuestion(): IFormQuestion {
        return this.formQuestions.find((question) => question.uid === this.condition.sourceQuestionUid);
    }

    constructor(
        private appTextService: AppTextService,
        private formConditionHelperService: FiFormConditionHelperService,
        private formHelperService: FiFormHelperService,
    ) {
        super();
    }

    getConditionActionLabel(conditionActionIndex: number): string {
        return this.formConditionHelperService.getConditionActionSelectLabel(conditionActionIndex);
    }

    isConditionTargetDisabled(): boolean {
        const conditionWithChildren = this.formConditionHelperService.getConditionWithChildren(this.condition);
        // if any of simple predicates has not filled out source question then we should disable selecting target
        return conditionWithChildren
            .filter((condition) => this.formConditionHelperService.isSimplePredicate(condition))
            .some((condition) => !isDefined(condition.sourceQuestionUid));
    }

    isTargetQuestionDisabled(question: IFormQuestion): boolean {
        return (
            (!this.condition.ChildItems.length && !this.condition.sourceQuestionUid
                ? false
                : this.isTargetEqualsSource(question)) || this.isQuestionSelectedAsTarget(question)
        );
    }

    private isQuestionSelectedAsTarget(question: IFormQuestion): boolean {
        return this.condition.Actions.some((conditionAction) => {
            return conditionAction.targetQuestionUid === question.uid;
        });
    }

    private isTargetEqualsSource(question: IFormQuestion): boolean {
        let conditions: IFormCondition[];
        conditions = this.condition.ChildItems.length
            ? this.formConditionHelperService.concatenateChildItems(this.condition, false)
            : [this.condition];
        return conditions.some((condition) => condition.sourceQuestionUid === question.uid);
    }

    isQuestionMissingValue(question: IFormQuestion): boolean {
        return this.formHelperService.isQuestionMissingValue(question, this.selectedLanguageId);
    }

    getQuestionName(question: IFormQuestion): string {
        return this.formHelperService.getQuestionName(question, this.selectedLanguageId, this.formQuestions);
    }

    addNewConditionAction() {
        this.condition.Actions.push(this.formHelperService.getNewConditionActionInstance());
    }

    removeConditionAction(conditionAction: IFormConditionAction) {
        const conditionActionIndex = this.condition.Actions.indexOf(conditionAction);
        this.condition.Actions.splice(conditionActionIndex, 1);
    }
}
